import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  ExclamationTriangleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

// Visual style per sync state. "blocked" and "not_synced" are warnings the user can act on,
// "error" means Tripletex rejected the last push, "pending" means it is queued.
const STATE_STYLES = {
  blocked: {
    wrap: "bg-amber-50 border-amber-500",
    title: "text-amber-800",
    text: "text-amber-700",
    iconColor: "text-amber-500",
    Icon: ExclamationTriangleIcon,
  },
  error: {
    wrap: "bg-red-50 border-red-500",
    title: "text-red-800",
    text: "text-red-700",
    iconColor: "text-red-500",
    Icon: XCircleIcon,
  },
  pending: {
    wrap: "bg-blue-50 border-blue-500",
    title: "text-blue-800",
    text: "text-blue-700",
    iconColor: "text-blue-500",
    Icon: ClockIcon,
  },
  not_synced: {
    wrap: "bg-amber-50 border-amber-500",
    title: "text-amber-800",
    text: "text-amber-700",
    iconColor: "text-amber-500",
    Icon: ExclamationTriangleIcon,
  },
};

/**
 * Renders a Tripletex sync warning banner for an order or customer.
 *
 * @param {Object}   sync      The `tripletex_sync` payload returned by the API.
 * @param {Function} onRetry   Optional async fn returning `{ tripletex_sync, message }`.
 * @param {String}   className Extra wrapper classes.
 */
export const TripletexSyncWarning = ({
  sync,
  onRetry,
  className = "",
  hideTitle = false,
}) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(sync);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    setCurrent(sync);
  }, [sync]);

  // Nothing to warn about: company not on Tripletex, already synced, or no data.
  if (!current || !current.enabled) return null;
  if (current.state === "synced" || current.state === "disabled") return null;

  const style = STATE_STYLES[current.state] || STATE_STYLES.not_synced;
  const Icon = style.Icon;
  const blockers = current.blockers || [];

  const handleRetry = async () => {
    if (!onRetry || retrying) return;
    try {
      setRetrying(true);
      const res = await onRetry();
      if (res?.tripletex_sync) {
        setCurrent(res.tripletex_sync);
      }
      toast.success(res?.message || t("tripletex_sync.retry_queued"));
    } catch (e) {
      toast.error(
        e?.response?.data?.error ||
          e?.message ||
          t("tripletex_sync.retry_failed")
      );
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div
      className={`shadow sm:overflow-hidden sm:rounded-md ${className}`}
      role="alert"
    >
      <div className={`px-6 py-4 border-l-4 ${style.wrap}`}>
        <div className="flex">
          <div className="flex-shrink-0">
            <Icon className={`h-5 w-5 ${style.iconColor}`} aria-hidden="true" />
          </div>
          <div className="ml-3 flex-1">
            {hideTitle ? null : (
              <h3 className={`text-sm font-medium ${style.title}`}>
                {current.title || t("tripletex_sync.title_default")}
              </h3>
            )}

            {current.hint && (
              <p className={`mt-1 text-sm ${style.text}`}>{current.hint}</p>
            )}

            {blockers.length > 0 && (
              <div className="mt-2">
                <ul className={`list-disc pl-5 space-y-1 text-sm ${style.text}`}>
                  {blockers.map((b, i) => (
                    <li key={b.code ? `${b.code}-${i}` : i}>
                      <span>{b.message}</span>
                      {b?.target?.type === "customer" && b?.target?.id && (
                        <Link
                          to={`/dashboard/customers/${b.target.id}`}
                          className="ml-2 font-medium underline hover:no-underline"
                        >
                          {t("tripletex_sync.open_customer")}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {current.can_retry && onRetry && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={handleRetry}
                  disabled={retrying}
                  className={`inline-flex items-center gap-1.5 rounded-md border border-transparent bg-white/70 px-3 py-1.5 text-sm font-medium shadow-sm hover:bg-white disabled:opacity-60 ${style.title}`}
                >
                  <ArrowPathIcon
                    className={`h-4 w-4 ${retrying ? "animate-spin" : ""}`}
                    aria-hidden="true"
                  />
                  {retrying
                    ? t("tripletex_sync.retrying")
                    : t("tripletex_sync.retry")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripletexSyncWarning;
